import { Action } from 'typescript-fsa';
import * as dotProp from 'dot-prop-immutable';

import { createReducer } from '@topconagriculture/frontend-utils';

import {
  DMReportItem,
  DryMatterEfficiencyItem,
  GroupDeliveryItem,
  IngredientDryMatterSettingChangesItem,
  IngredientPricesSettingChangesItem,
  IngredientSettingChangesItem,
  IngredientUsageByPenItem,
  IngredientUsageReportItem,
  InventoryItem,
  LoadReportItem,
  OperatorAccuracyReportItem,
  PenSettingChangesItem,
  RationDeliveryItem,
  RationSettingChangesItem,
  Report,
  ReportFilterSettings,
  SortingOrder,
} from './reports.types';
import {
  disableDownload,
  getReportData,
  getReportFilterData,
  updateInventoryReportRow,
  setSortingColumn,
  setSortingOrder,
  updateTransactionsReportRow,
} from './reports.actions';
import { DatatoolReportType, InventoryReportType, ReportType } from './reports.constants';
import { InventoryReportData, StockTransactionItem } from '../inventory/inventory.type';
import { deleteRecord, updateRecord } from '../orders/orders.actions';

export type ReportsState = {
  [ReportType.DryMatterEfficiency]: Report<DryMatterEfficiencyItem> | null;
  [ReportType.DMIntakePerHead]: Report<DMReportItem> | null;
  [ReportType.GroupDelivery]: Report<GroupDeliveryItem> | null;
  [ReportType.IngredientsPremixUsage]: Report<IngredientUsageReportItem> | null;
  [ReportType.LoadReport]: Report<LoadReportItem> | null;
  [ReportType.OperatorAccuracy]: Report<OperatorAccuracyReportItem> | null;
  [ReportType.RationDelivery]: Report<RationDeliveryItem> | null;
  [ReportType.IngredientUsageByPen]: Report<IngredientUsageByPenItem> | null;
  [DatatoolReportType.PenSettingChanges]: Report<PenSettingChangesItem> | null;
  [DatatoolReportType.RationSettingChanges]: Report<RationSettingChangesItem> | null;
  [DatatoolReportType.IngredientSettingChanges]: Report<IngredientSettingChangesItem> | null;
  [DatatoolReportType.IngredientPriceSettingChanges]: Report<IngredientPricesSettingChangesItem> | null;
  [DatatoolReportType.IngredientDryMatterSettingChanges]: Report<IngredientDryMatterSettingChangesItem> | null;
  [InventoryReportType.Inventory]: Report<InventoryItem> | null;
  [InventoryReportType.InventoryProjectedRequirements]: Report<InventoryReportData> | null;
  [InventoryReportType.StockTransaction]: Report<StockTransactionItem> | null;
  downloadButtonDisabled: boolean | null;
  filterSettings: ReportFilterSettings | null;
  sortingColumn: string | undefined;
  sortingOrder: SortingOrder | undefined;
};

export const initialState: ReportsState = {
  [ReportType.DryMatterEfficiency]: null,
  [ReportType.DMIntakePerHead]: null,
  [ReportType.GroupDelivery]: null,
  [ReportType.IngredientsPremixUsage]: null,
  [ReportType.LoadReport]: null,
  [ReportType.OperatorAccuracy]: null,
  [ReportType.RationDelivery]: null,
  [ReportType.IngredientUsageByPen]: null,
  [DatatoolReportType.PenSettingChanges]: null,
  [DatatoolReportType.RationSettingChanges]: null,
  [DatatoolReportType.IngredientSettingChanges]: null,
  [DatatoolReportType.IngredientPriceSettingChanges]: null,
  [DatatoolReportType.IngredientDryMatterSettingChanges]: null,
  [InventoryReportType.Inventory]: null,
  [InventoryReportType.InventoryProjectedRequirements]: null,
  [InventoryReportType.StockTransaction]: null,
  downloadButtonDisabled: null,
  filterSettings: null,
  sortingColumn: undefined,
  sortingOrder: undefined,
};

const reportsReducer = createReducer(initialState, {
  [getReportData.done.type]: (state: ReportsState, action: Action<{ params: any; result: any }>) => {
    return dotProp.set(state, action.payload.params.type, action.payload.result);
  },
  [disableDownload.type]: (state: ReportsState, action: ReturnType<typeof disableDownload>): ReportsState => {
    return {
      ...state,
      downloadButtonDisabled: action.payload,
    };
  },
  [getReportFilterData.done.type]: (state: ReportsState, action: Action<{ params: any; result: any }>) => {
    return dotProp.set(state, 'filterSettings', action.payload.result);
  },
  [updateInventoryReportRow.done.type]: (
    state: ReportsState,
    action: ReturnType<typeof updateInventoryReportRow.done>,
  ) => {
    const ingredient = action.payload.result;
    const newState = state.inventory!.reportData.gridData!.items.map((elem) =>
      elem.ingredientId === ingredient.id
        ? {
            ...elem,
            stockDaysLeft: ingredient.stockDaysLeft,
            averageDailyUsage: ingredient.avgDailyUsage,
            stockPerc: (ingredient.stock! / ingredient.stockMax!) * 100,
            stock: ingredient.stock,
            maxStock: ingredient.stockMax,
            dryStock: ingredient.stockDry,
            totalPrice: ingredient.totalPrice,
          }
        : elem,
    );
    return dotProp.set(state, `${[InventoryReportType.Inventory]}.reportData.gridData.items`, newState);
  },
  [updateTransactionsReportRow.done.type]: (
    state: ReportsState,
    action: ReturnType<typeof updateTransactionsReportRow.done>,
  ) => {
    const receiveIngredient = action.payload.result;
    const summaryWeightMultiplier = action.payload.params;
    let oldItem;
    const newItems = state?.stocktransaction?.reportData.gridData!.items.map((item) => {
      if (item.id === receiveIngredient?.id && item.stockTransactionId === receiveIngredient?.stockTransactionId) {
        oldItem = { ...item };
        return { ...item, ...receiveIngredient };
      }
      return item;
    });
    const newGroupSummaries = state?.stocktransaction?.reportData.gridData.groupSummaries?.map((summary) =>
      summary.key === receiveIngredient.ingredientId
        ? {
            ...summary,
            items: [
              {
                ...summary.items[0],
                actualWeight:
                  summary.items[0].actualWeight - oldItem.actualWeight + (receiveIngredient.actualWeight || 0),
                totalActualPrice:
                  summary.items[0].totalActualPrice -
                  oldItem.totalActualPrice +
                  (receiveIngredient.totalActualPrice || 0),
              },
            ],
          }
        : summary,
    );
    const summaryItem = { ...state?.stocktransaction?.reportData.gridData!.summaryItems![0] };
    let newState = dotProp.set(state, `${[InventoryReportType.StockTransaction]}.reportData.gridData.items`, newItems);
    newState = dotProp.set(
      { ...newState },
      `${[InventoryReportType.StockTransaction]}.reportData.gridData.groupSummaries`,
      newGroupSummaries,
    );

    const newSummaryItem = [
      {
        ...summaryItem,
        actualWeight:
          (summaryItem.actualWeight || 0) -
          oldItem.actualWeight * summaryWeightMultiplier +
          (receiveIngredient.actualWeight || 0) * summaryWeightMultiplier,
        totalActualPrice:
          (summaryItem.totalActualPrice || 0) - oldItem.totalActualPrice + (receiveIngredient.totalActualPrice || 0),
      },
    ];
    newState = dotProp.set(
      { ...newState },
      `${[InventoryReportType.StockTransaction]}.reportData.gridData.summaryItems`,
      newSummaryItem,
    );
    return newState;
  },
  [updateRecord.done.type]: (state: ReportsState, action: ReturnType<typeof updateRecord.done>) => {
    const stocktransaction = action.payload.result;
    const newState = state.stocktransaction!.reportData.gridData!.items.filter(
      (item) => !(item.id === stocktransaction.id && item.stockTransactionId === stocktransaction.stockTransactionId),
    );
    return dotProp.set(state, `${[InventoryReportType.StockTransaction]}.reportData.gridData.items`, newState);
  },
  [deleteRecord.done.type]: (state: ReportsState, action: ReturnType<typeof deleteRecord.done>) => {
    const { stockTransactionId, stockTransactionitemId } = action.payload.params;
    const newState = state.stocktransaction!.reportData.gridData!.items.filter(
      (item) => !(item.id === stockTransactionitemId && item.stockTransactionId === stockTransactionId),
    );
    return dotProp.set(state, `${[InventoryReportType.StockTransaction]}.reportData.gridData.items`, newState);
  },
  [setSortingColumn.type]: (state: ReportsState, action: ReturnType<typeof setSortingColumn>) => {
    return dotProp.set(state, 'sortingColumn', action.payload);
  },
  [setSortingOrder.type]: (state: ReportsState, action: ReturnType<typeof setSortingOrder>) => {
    return dotProp.set(state, 'sortingOrder', action.payload);
  },
});

export default reportsReducer;
